import { lookupImpersonationTarget } from '../pages/Admin/impersonationApi';
import { abandonImpersonation, startImpersonation, type ImpersonationTarget } from './impersonation';

export type FollowDeps = {
  lookup: typeof lookupImpersonationTarget;
  start: (target: ImpersonationTarget) => boolean;
  abandon: () => void;
};

const defaultDeps: FollowDeps = {
  lookup: lookupImpersonationTarget,
  start: (target) => startImpersonation(target, () => undefined),
  abandon: abandonImpersonation,
};

export const FOLLOW_LOOKUP_TIMEOUT_MS = 4000;

function withinDeadline<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('imitation lookup timed out')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    );
  });
}

export async function followHandoffImitation(
  email: string | null,
  token: string,
  { timeoutMs = FOLLOW_LOOKUP_TIMEOUT_MS }: { timeoutMs?: number } = {},
  deps: FollowDeps = defaultDeps
): Promise<boolean> {
  if (!email) {
    deps.abandon();
    return false;
  }
  let target: ImpersonationTarget | null = null;
  try {
    const found = await withinDeadline(deps.lookup(email, { asActor: true, token }), timeoutMs);
    if (found.eligible && found.sub) {
      target = { sub: found.sub, email: found.email ?? email, districtName: found.districtName };
    }
  } catch {
    target = null;
  }
  if (!target || !deps.start(target)) {
    deps.abandon();
    return false;
  }
  return true;
}
